
import { useToast } from '@shared/lib/toast'
import { useCommonStore } from '@shared/model'
import { trackEvent } from '@shared/lib/amplitude'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { useDisciplineInfoUpdateApi } from '../api/update-discipline-info.hook'
import { useFeedbackCreateApi } from '../api/create'
import { useFeedbackStepQuery } from './feedback-step-query.hook'
import { useFeedbackStore } from './store'
import { FeedbackStep } from './store/enums'

interface ButtonSendFeedbackReturn {
  name: string
  text: string
  show: ComputedRef<boolean>
  setSubmit: () => void
}

const STEPS_WITH_BUTTON: FeedbackStep[] = [
  FeedbackStep.Rating,
  FeedbackStep.ClassroomRating,
  FeedbackStep.TutorTransition,
  FeedbackStep.DetailedFeedback,
  FeedbackStep.AdditionalPractice,
]

export const useButtonSendFeedback = (): ButtonSendFeedbackReturn => {
  const name = 'btn_feedback_send'
  const text = 'Send feedback'

  const feedbackStore = useFeedbackStore()
  const commonStore = useCommonStore()
  const toast = useToast()

  const { handleNext } = useFeedbackStepQuery()

  const isVisibleStep = computed(() => STEPS_WITH_BUTTON.includes(feedbackStore.getStep))

  const show = computed(() => isVisibleStep.value && feedbackStore.canSendFeedback)

  const setDisciplineUpdate = async (): Promise<void> => {
    const requestData = {
      disciplineId: feedbackStore.getDiscipline!.id,
      data: {
        discipline: {
          additional_practice: feedbackStore.isAdditionalPracticeRequested,
        },
      },
    }

    await useDisciplineInfoUpdateApi(requestData, {
      onSuccess: () => {
        handleNext()
        commonStore.setLoading(false)
      },
      onError: () => {
        toast.error('Unexpected error. Failed to update additional practice preference.')
        handleNext()
        commonStore.setLoading(false)
      },
    })
  }

  const sendFeedback = (): void => {
    void useFeedbackCreateApi({
      onStart: () => {
        commonStore.setLoading(true)
      },
      onSuccess: async () => {
        trackEvent('Request_success', { ...feedbackStore.getEventProps, feature: 'feedback', name: 'feedback_create' })
        if (feedbackStore.hasAdditionalPracticeInteraction) {
          await setDisciplineUpdate()
        } else {
          handleNext()
          commonStore.setLoading(false)
        }
      },
      onError: () => {
        trackEvent('Request_error', { ...feedbackStore.getEventProps, feature: 'feedback', name: 'feedback_create' })
        commonStore.setLoading(false)
      },
    })
  }

  const setSubmit = (): void => {
    const step = toAnalyticsId(feedbackStore.getStep)
    const isValid = !feedbackStore.isCommentRequiredAndEmpty

    trackEvent('Form_submit', {
      ...feedbackStore.getEventProps,
      feature: 'feedback',
      name,
      step,
      is_valid: isValid,
      rating: feedbackStore.getRating,
    })

    if (!isValid) {
      trackEvent('Form_error', { feature: 'feedback', name, step, error_code: 'comment_required' })
      feedbackStore.setCommentError(true)
      toast.warning('Please add a comment to describe your issue.')
      return
    }

    if (feedbackStore.isLastStep) {
      sendFeedback()
    } else {
      handleNext()
    }
  }

  return {
    name,
    text,
    show,
    setSubmit,
  }
}
